import { useState, useEffect } from 'react';
import { Container, Grid, Button, Checkbox, GridCol, Group } from '@mantine/core';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import { Language } from '../../languages/languageHandler';
import { ICurrency } from '../../../libraries/currencies';
import CurrencyInput from '../../components/currency-input';
import { StatusOper } from '../../../libraries/zps';
import StatusOperModal from '../../components/status-oper-modal';
import { IUserSummary, IUserSummaryRequest, updateUserSummary } from '../../../libraries/brisa';
import DisplayField from '../../components/display-field';
import { formatCurrAmount, formatTransactionType } from '../../utils/script';
import { formatDateTimeLabel } from '../../utils/date';
import './styles.scss';

interface AddEditUserSummaryProps {
  userSummary?: IUserSummary;
  mode?: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditUserSummary = ({ userSummary, mode, opened, close, onSaved }: AddEditUserSummaryProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currency = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();

  const [formData, setFormData] = useState<IUserSummaryRequest>({
    eid: userSummary?.lastOperationEid ?? 0,
    entid: userSummary?.entid || 0,
    incOrSet: true,
    uid: userSummary?.uid || 0,
    value: 0
  });
  const [loading, setLoading] = useState(false);

  const [statusOper, setStatusOper] = useState<StatusOper | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [actionType, setActionType] = useState<'add' | 'edit' | 'delete'>('add');

  // Populate formData when product or mode changes
  useEffect(() => {
    if (userSummary) {
      setFormData({
        eid: userSummary.lastOperationEid,
        entid: userSummary.entid,
        incOrSet: true,
        uid: userSummary.uid,
        value: 0
      });
    }
  }, [userSummary]);

  const showStatus = (status: StatusOper) => {
    setStatusOper(status);
    setShowStatusModal(true);

    if (status.code === 0) {
      onSaved?.();

      setTimeout(() => {
        setShowStatusModal(false);
        close();
      }, 2000);
    }
  };

  // Update form field
  const handleChange = (field: keyof IUserSummaryRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save product (add or edit)
  const handleSave = async () => {
    setLoading(true);
    setActionType(mode === 'new' ? 'add' : 'edit');
    try {
      const response = await updateUserSummary(formData, controller);

      showStatus(response.statusOper);
    } catch (err: any) {
      const statusOper: StatusOper = err?.response?.data?.statusOper ?? {
        code: -1,
        message: err.message || 'An unexpected error occurred'
      };
      showStatus(statusOper);
    } finally {
      setLoading(false);
    }
  };

  const messageMap = {
    add: {
      success: lang.BRISA_USER_SUMMARY_ADD_SUCCESS,
      error: lang.BRISA_USER_SUMMARY_ADD_ERROR
    },
    edit: {
      success: lang.BRISA_USER_SUMMARY_EDIT_SUCCESS,
      error: lang.BRISA_USER_SUMMARY_EDIT_ERROR
    },
    delete: {
      success: lang.BRISA_USER_SUMMARY_DELETE_SUCCESS,
      error: lang.BRISA_USER_SUMMARY_DELETE_ERROR
    }
  };

  const message = messageMap[actionType];

  const title =
    mode === 'new' ? `${lang.BRISA_USER_SUMMARY_ADD}` : `${lang.BRISA_USER_SUMMARY_EDIT} - ${userSummary?.uid}`;

  return (
    <>
      <ModalComponent
        show={opened}
        handleClick={close}
        title={title}
        content={
          <Container className="user-summary-modal">
            <Grid>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField title={lang.BRISA_USERS_SUMMARIES_USER_ID} content={userSummary?.uid} />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField title={lang.BRISA_USERS_SUMMARIES_USER_NAME} content={userSummary?.name} />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField
                  title={lang.BRISA_USERS_SUMMARIES_LAST_OPER}
                  content={formatTransactionType(lang, userSummary?.lastOperation)}
                />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField
                  title={lang.BRISA_USERS_SUMMARIES_EID_LAST_OPER}
                  content={userSummary?.lastOperationEid}
                />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField
                  title={lang.BRISA_USERS_SUMMARIES_DATE_LAST_OPER}
                  content={formatDateTimeLabel(userSummary?.lastOperationDt)}
                />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField
                  title={lang.BRISA_USERS_SUMMARIES_AMOUNT_LAST_OPER}
                  content={formatCurrAmount(userSummary?.lastOperationValue || 0, currency[0])} //Currency default to EUR
                />
              </GridCol>
              <GridCol span={{ base: 12, xs: 6, sm: 6, md: 4 }}>
                <DisplayField
                  title={lang.BRISA_USERS_SUMMARIES_CURRENT_BALANCE}
                  content={formatCurrAmount(userSummary?.balance || 0, currency[0])} //Currency default to EUR
                />
              </GridCol>
            </Grid>

            <Grid className="editable-values">
              <GridCol span={12}>
                <Checkbox
                  label={lang.BRISA_USERS_SUMMARIES_BALANCE_ZERO}
                  checked={!formData.incOrSet}
                  onChange={e => handleChange('incOrSet', !e.currentTarget.checked)}
                />
              </GridCol>

              <GridCol span={4}>
                <CurrencyInput
                  value={formData.value}
                  label={lang.BRISA_USERS_SUMMARIES_AMOUNT_ADD}
                  currencySymbol={currency[0]?.currencyStr}
                  onChange={value => handleChange('value', value)}
                />
              </GridCol>
            </Grid>

            <Group className="buttons">
              <Button onClick={handleSave}>{lang.GLOBAL_SAVE_BUTTON}</Button>
            </Group>
          </Container>
        }
      />

      {statusOper && (
        <StatusOperModal
          opened={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          statusOper={statusOper}
          successMessage={statusOper.code === 0 ? message?.success : undefined}
          errorMessage={statusOper.code !== 0 ? message?.error : undefined}
        />
      )}
    </>
  );
};

export default AddEditUserSummary;
